import {
	Component,
	ElementRef,
	EventEmitter,
	Input,
	OnChanges,
	OnInit,
	Output,
	SimpleChanges,
	ViewChild,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { NzModalService } from 'ng-zorro-antd/modal';
import { AttestationFinDeFormation } from '@app/models/attestation-fin-de-formation/attestation-fin-de-formation';
import { AttestationFinDeFormationService } from '@app/models/attestation-fin-de-formation/attestation-fin-de-formation.service';
import { AttestationFinDeFormationSearchParams } from '@app/models/attestation-fin-de-formation/attestation-fin-de-formation-search-params';
import { User } from '@app/models/user/user';
import { DocumentAdministrative } from '@app/models/document-administrative';
import { FormationService } from '@app/models/formation';
import { PresenceSummaryResponse } from '@app/models/formation-session/formation-session.service';

// Simplified interface for attestation fin de formation
interface SimpleAttestationFinFormation {
	_id?: string;
	owner: User | string; // Owner/stagiaire
	document?: any;
	status?: string;
	created_at?: Date;
	total_time?: number;
	total_time_override?: number;
	total_time_percent?: number;
	total_presence?: number; // From presence endpoint
	total_hours?: number; // From presence endpoint
}

@Component({
	selector: 'app-atom-formation-table-attestation-fin-formation',
	templateUrl:
		'./atom-formation-table-attestation-fin-formation.component.html',
	styleUrls: [
		'./atom-formation-table-attestation-fin-formation.component.less',
	],
})
export class AtomFormationTableAttestationFinFormationComponent
	implements OnInit, OnChanges
{
	@Input() title: string;
	@Input() formationId: string;
	@Input() respondants: User[] = [];
	@Input() disableUpload = true; // Disabled by default
	@Input() disableGenerate: boolean;
	@Input() disableSend: boolean;
	@Input() disableDelete = true;
	@Input() generateLoading = false;
	@Input() templateOptions: string[] = []; // Available template options
	@Input() defaultTemplate: string | null = null;
	@Output() emitTemplateChange = new EventEmitter<string>();
	@Output() emitGenerate = new EventEmitter<{
		docType: string;
		template: string;
		totalTimeOverride?: number;
	}>();
	@Output() emitGenerateForRespondant = new EventEmitter<{
		docType: string;
		respondantId: string;
		template: string;
		totalTimeOverride?: number;
	}>();

	@Output() emitReadDocument = new EventEmitter<string>();
	@Output() emitSendDocument = new EventEmitter<string>();
	@Output() emitDeleteDocument = new EventEmitter<string>();
	@Output() emitDownloadDocument = new EventEmitter<{
		attestationId: string;
		filename: string;
	}>();
	@Output() emitSendAllDocuments = new EventEmitter<string[]>();

	// Bulk action events
	@Output() emitBulkGenerate = new EventEmitter<{
		selectedIds: string[];
		partialRespondantIds: string[];
		docType: string;
		template: string;
		totalTimeOverride?: number;
	}>();
	@Output() emitBulkSend = new EventEmitter<string[]>();
	@Output() emitBulkDelete = new EventEmitter<string[]>();
	@Output() emitBulkDownload = new EventEmitter<
		{ attestationId: string; filename: string }[]
	>();

	@ViewChild('btnsContainer') btnsContainer: ElementRef;

	// Modal properties
	showGenerateModal = false;
	showRespondantModal = false;
	selectedTemplate: string = '';
	totalTimeOverride: number = null;
	globalTimeOverride: number = null;
	respondantTimeOverride: number = null;
	respondantDefaultTimeOverride: number = null;
	defaultTimeOverride: number = null;
	respondantModalTitle: string = '';
	currentRespondantId: string = '';

	// Store previous overrides by respondant
	previousOverrides: { [respondantId: string]: number } = {};

	attestations: AttestationFinDeFormation[] = [];
	displayItems: SimpleAttestationFinFormation[] = [];
	loading = false;

	// Store presence summary from formation presence endpoint
	presenceSummaryData: PresenceSummaryResponse = null;

	// Selection state management
	selectedItems: Record<string, boolean> = {};
	selectAll = false;

	// Cached properties for performance
	private _selectedCount = 0;
	private _hasSelected = false;
	private _canSend = true;
	private _canDelete = false;
	private _canDownload = false;
	private _hasPartial = false;

	constructor(
		public translateService: TranslateService,
		private attestationFinDeFormationService: AttestationFinDeFormationService,
		private formationService: FormationService,
		private nzModalService: NzModalService
	) {}

	async ngOnInit(): Promise<void> {
		// Initialize selectedTemplate if templateOptions are available
		if (this.templateOptions && this.templateOptions.length > 0) {
			this.selectedTemplate =
				this.defaultTemplate || this.templateOptions[0];
		}

		// Get default time override
		if (this.formationId) {
			this.defaultTimeOverride =
				await this.calculateDefaultTimeOverride();

			this.globalTimeOverride = this.defaultTimeOverride;
		}

		await this.refresh();
	}

	async ngOnChanges(changes: SimpleChanges): Promise<void> {
		if (
			(changes.formationId && !changes.formationId.firstChange) ||
			(changes.respondants && !changes.respondants.firstChange)
		) {
			if (changes.formationId) {
				// Update default time override if formation ID changes
				this.defaultTimeOverride =
					await this.calculateDefaultTimeOverride();
				this.globalTimeOverride = this.defaultTimeOverride;
			}
			await this.refresh();
		}

		// Initialize selectedTemplate when templateOptions changes
		if (
			changes.templateOptions &&
			this.templateOptions &&
			this.templateOptions.length > 0
		) {
			this.selectedTemplate = this.templateOptions[0];
		}
	}

	// Calculate default time override based on formation presence data
	async calculateDefaultTimeOverride(): Promise<number> {
		if (!this.formationId) return 0;

		try {
			const presenceSummary =
				await this.formationService.getPresenceSummaryAsAdmin(
					this.formationId
				);
			this.presenceSummaryData = presenceSummary;
			return presenceSummary.total_hours || 0;
		} catch (error) {
			console.error('Error calculating default time override:', error);
			return 0;
		}
	}

	// Get time override for a specific respondant
	getTimeOverrideForRespondant(respondantId: string): number {
		// If we have a previous override for this respondant, use it
		if (this.previousOverrides[respondantId]) {
			return this.previousOverrides[respondantId];
		}

		// Otherwise use the default calculation
		return this.defaultTimeOverride;
	}

	// Get total_presence for a specific respondant from presence summary
	getRespondantPresence(respondantId: string): number | null {
		if (
			!this.presenceSummaryData ||
			!this.presenceSummaryData.respondants
		) {
			return null;
		}

		const respondantData = this.presenceSummaryData.respondants.find(
			(r) => r.respondant._id === respondantId
		);

		return respondantData ? respondantData.total_presence : null;
	}

	// Update previous overrides from attestation data
	updatePreviousOverrides(): void {
		if (!this.attestations || this.attestations.length === 0) return;

		this.attestations.forEach((attestation) => {
			if (attestation.stagiaireExists() && attestation.documentExists()) {
				const stagiaire = attestation.props.stagiaire;
				const document = attestation.props.document;

				if (
					stagiaire instanceof User &&
					document instanceof DocumentAdministrative
				) {
					const stagiaireId = stagiaire.props._id;

					// Get total_time_override from document's data
					if (document.props.data && document.props.data.formation) {
						const totalTimeOverride =
							document.props.data.formation.total_time_override;
						if (totalTimeOverride) {
							this.previousOverrides[stagiaireId] =
								totalTimeOverride;
						}
					}
				}
			}
		});
	}

	async refresh(): Promise<void> {
		if (!this.formationId) {
			this.updateDisplayItems();
			return;
		}

		this.loading = true;

		const searchParams = new AttestationFinDeFormationSearchParams();
		const searchParamsObj: any = {
			_page: 0,
			_limit: 100,
			_populate: ['stagiaire', 'document'],
			formation: this.formationId,
		};

		searchParams.fromObject(searchParamsObj);

		const resultItems = await this.attestationFinDeFormationService
			.listAsAdmin(searchParams.toObject())
			.then((result) => result.items)
			.catch((error) => {
				console.error('Error loading attestations:', error);
				this.updateDisplayItems();
				this.loading = false;

				return [];
			});

		this.attestations = resultItems;
		this.updatePreviousOverrides();
		this.updateDisplayItems();
		this.loading = false;
	}

	updateDisplayItems(): void {
		this.displayItems = [];

		// Add all attestations
		if (this.attestations && this.attestations.length > 0) {
			this.attestations.forEach((attestation) => {
				let document = null;
				let status = null;
				let created_at = null;
				let total_time = null;
				let total_time_override = null;
				let total_time_percent = null;
				if (attestation.documentExists()) {
					const doc = attestation.props.document;
					if (doc instanceof DocumentAdministrative) {
						document = doc;
						status = doc.props.status;
						created_at = attestation.props.created_at;

						// Get total_time and total_time_override from document's data
						if (doc.props.data && doc.props.data.formation) {
							total_time = doc.props.data.formation.total_time;
							total_time_override =
								doc.props.data.formation.total_time_override;
							total_time_percent =
								doc.props.data.formation.total_time_percent;
						}
					}
				}

				let owner = null;
				if (attestation.stagiaireExists()) {
					owner = attestation.props.stagiaire;
				} else if (attestation.props.stagiaire) {
					owner = attestation.props.stagiaire;
				}

				if (owner) {
					// Get respondant ID and presence data from presence endpoint
					const respondantId =
						owner instanceof User
							? owner.props._id
							: (owner as string);

					const total_presence =
						this.getRespondantPresence(respondantId);
					const total_hours = this.presenceSummaryData
						? this.presenceSummaryData.total_hours
						: null;

					this.displayItems.push({
						_id: attestation.props._id,
						owner,
						document,
						status,
						created_at,
						total_time,
						total_time_override,
						total_time_percent,
						total_presence,
						total_hours,
					});
				}
			});
		}

		// Add respondants without attestations
		if (this.respondants && this.respondants.length > 0) {
			this.respondants.forEach((respondant) => {
				// Check if the respondant already has an attestation
				const existingAttestation = this.displayItems.find(
					(item) =>
						item.owner instanceof User &&
						respondant instanceof User &&
						item.owner.props._id === respondant.props._id
				);

				// If not, add it to the display items with presence data
				if (!existingAttestation) {
					const total_presence = this.getRespondantPresence(
						respondant.props._id
					);
					const total_hours = this.presenceSummaryData
						? this.presenceSummaryData.total_hours
						: null;

					this.displayItems.push({
						owner: respondant,
						total_presence,
						total_hours,
					});
				}
			});
		}

		// Clean up selected items that no longer exist
		this.cleanupSelectedItems();

		// Refresh selection state for new data
		this.refreshSelectionState();
	}

	// Clean up selected items that no longer exist in displayItems
	private cleanupSelectedItems(): void {
		const validItemIds = new Set<string>();

		// Collect all valid item IDs
		this.displayItems.forEach((item) => {
			const itemId = this.getItemId(item);
			if (itemId) {
				validItemIds.add(itemId);
			}
		});

		// Remove selected items that are no longer valid
		Object.keys(this.selectedItems).forEach((selectedId) => {
			if (!validItemIds.has(selectedId)) {
				delete this.selectedItems[selectedId];
			}
		});
	}

	getOwnerName(item: SimpleAttestationFinFormation): string {
		if (!item.owner) return '';

		if (item.owner instanceof User) {
			return `${item.owner.props.first_name} ${item.owner.props.last_name}`;
		}

		// If the owner is a string (ID) or another object type
		return item.owner.toString();
	}

	getDocumentLabel(item: SimpleAttestationFinFormation): string {
		if (!item.document) return '';

		if (item.document instanceof DocumentAdministrative) {
			return item.document.props.label || '';
		}

		return '';
	}

	getDocumentId(item: SimpleAttestationFinFormation): string {
		if (!item.document) return '';

		if (item.document instanceof DocumentAdministrative) {
			return item.document.props._id || '';
		}

		if (typeof item.document === 'string') {
			return item.document;
		}

		return '';
	}

	getDocumentStatus(item: SimpleAttestationFinFormation): string {
		if (!item.document) return '';

		if (item.document instanceof DocumentAdministrative) {
			return item.document.props.status || '';
		}

		return item.status || '';
	}

	getCreatedDate(item: SimpleAttestationFinFormation): Date {
		return item.created_at || null;
	}

	canDownload(item: SimpleAttestationFinFormation): boolean {
		return !!item.document;
	}

	// Check if an item is sendable (has generated document)
	canSendItem(item: SimpleAttestationFinFormation): boolean {
		if (!item.document) return false;
		const status = this.getDocumentStatus(item);
		return status === 'generated' || status === 'created';
	}

	// Check if any items are sendable when no selection
	hasAnySendableItems(): boolean {
		return this.displayItems.some((item) => this.canSendItem(item));
	}

	// Check if all selected items are sendable
	areAllSelectedItemsSendable(): boolean {
		const selectedKeys = Object.keys(this.selectedItems).filter(
			(key) => this.selectedItems[key]
		);

		// If no items selected, check if there are any sendable items
		if (selectedKeys.length === 0) {
			return this.hasAnySendableItems();
		}

		// Check ALL selected items (including partial items)
		// If ANY selected item is not sendable, return false
		return selectedKeys.every((key) => {
			if (key.startsWith('partial_')) {
				// Partial items are never sendable
				return false;
			}

			const item = this.displayItems.find((item) => item._id === key);
			return item && this.canSendItem(item);
		});
	}

	// Check if any items are downloadable when no selection
	hasAnyDownloadableItems(): boolean {
		return this.displayItems.some((item) => this.canDownload(item));
	}

	// Check if all selected items are downloadable
	areAllSelectedItemsDownloadable(): boolean {
		const selectedKeys = Object.keys(this.selectedItems).filter(
			(key) => this.selectedItems[key]
		);

		// If no items selected, check if there are any downloadable items
		if (selectedKeys.length === 0) {
			return this.hasAnyDownloadableItems();
		}

		// Check ALL selected items (including partial items)
		// If ANY selected item is not downloadable, return false
		return selectedKeys.every((key) => {
			if (key.startsWith('partial_')) {
				// Partial items are never downloadable
				return false;
			}

			const item = this.displayItems.find((item) => item._id === key);
			return item && this.canDownload(item);
		});
	}

	openRespondantModal(item: SimpleAttestationFinFormation): void {
		if (!(item.owner instanceof User)) return;

		this.respondantModalTitle = this.getOwnerName(item);
		this.currentRespondantId = item.owner.props._id;

		// Set default time override for this respondant using their presence data
		this.respondantDefaultTimeOverride = this.getRespondantPresence(
			this.currentRespondantId
		);

		// Fallback to previous override or default if presence is not available
		if (
			this.respondantDefaultTimeOverride === null ||
			this.respondantDefaultTimeOverride === undefined
		) {
			this.respondantDefaultTimeOverride =
				this.getTimeOverrideForRespondant(this.currentRespondantId);
		}

		this.respondantTimeOverride = this.respondantDefaultTimeOverride;

		this.showRespondantModal = true;
	}

	handleGenerateSubmit(): void {
		if (!this.selectedTemplate) return;

		this.emitGenerate.emit({
			docType: 'attestation-fin-formation',
			template: this.selectedTemplate,
			totalTimeOverride: this.globalTimeOverride,
		});

		this.showGenerateModal = false;
		this.totalTimeOverride = null;
	}

	handleRespondantGenerateSubmit(): void {
		if (!this.selectedTemplate || !this.currentRespondantId) return;

		this.emitGenerateForRespondant.emit({
			docType: 'attestation-fin-formation',
			respondantId: this.currentRespondantId,
			template: this.selectedTemplate,
			totalTimeOverride: this.respondantTimeOverride,
		});

		this.showRespondantModal = false;
		this.totalTimeOverride = null;
	}

	onReadDocument(id: string): void {
		this.emitReadDocument.emit(id);
	}

	onSendDocument(id: string): void {
		this.emitSendDocument.emit(id);
	}

	onDeleteDocument(id: string): void {
		this.nzModalService.confirm({
			nzTitle: this.translateService.instant(
				'modal_delete_document_title'
			),
			nzContent: this.translateService.instant(
				'modal_delete_document_content'
			),
			nzOkText: this.translateService.instant('common_delete'),
			nzOkType: 'danger',
			nzCancelText: this.translateService.instant('common_cancel'),
			nzOnOk: () => {
				this.emitDeleteDocument.emit(id);
			},
		});
	}

	onDownloadDocument(attestationId: string, filename: string): void {
		this.emitDownloadDocument.emit({
			attestationId,
			filename,
		});
	}

	hasGeneratedDocuments(): boolean {
		// Check if there are any generated documents to send
		return this.displayItems.some((item) => {
			if (!item.document) return false;

			const status = this.getDocumentStatus(item);
			return status === 'generated' || status === 'created';
		});
	}

	onSendAllDocuments(): void {
		// Collect all attestation IDs that have documents in 'generated' or 'created' status
		const attestationIds = this.displayItems
			.filter((item) => {
				if (!item._id || !item.document) return false;

				const status = this.getDocumentStatus(item);
				return status === 'generated' || status === 'created';
			})
			.map((item) => item._id);

		if (attestationIds.length === 0) return;

		this.emitSendAllDocuments.emit(attestationIds);
	}

	onTemplateChange(template: string): void {
		this.selectedTemplate = template;
		this.emitTemplateChange.emit(template);
	}

	// ====== SELECTION MANAGEMENT ======

	// Cached properties getters
	get selectedCount(): number {
		return this._selectedCount;
	}

	get hasSelected(): boolean {
		return this._hasSelected;
	}

	get canSend(): boolean {
		return this._canSend;
	}

	get canDelete(): boolean {
		return this._canDelete;
	}

	get canBulkDownload(): boolean {
		return this._canDownload;
	}

	get hasPartial(): boolean {
		return this._hasPartial;
	}

	// Helper methods
	getItemId(item: SimpleAttestationFinFormation): string {
		if (item._id) {
			return item._id; // Real attestation ID
		}
		// For partial items (respondants without attestations)
		if (item.owner instanceof User) {
			return `partial_${item.owner.props._id}`;
		}
		return '';
	}

	isItemSelected(item: SimpleAttestationFinFormation): boolean {
		const itemId = this.getItemId(item);
		return !!this.selectedItems[itemId];
	}

	private refreshSelectionState(): void {
		const selectedKeys = Object.keys(this.selectedItems).filter(
			(key) => this.selectedItems[key]
		);

		this._selectedCount = selectedKeys.length;
		this._hasSelected = this._selectedCount > 0;

		// Check if any selected items are partial (respondants without attestations)
		this._hasPartial = selectedKeys.some((key) =>
			key.startsWith('partial_')
		);

		// Can send: based on new requirements - all selected items must be sendable
		this._canSend = this.areAllSelectedItemsSendable();

		// Can delete: only if has selected items AND no partial items
		this._canDelete = this._hasSelected && !this._hasPartial;

		// Can download: similar to canSend logic - all selected items must be downloadable
		this._canDownload = this.areAllSelectedItemsDownloadable();

		// Update selectAll checkbox state
		this.selectAll =
			this.displayItems.length > 0 &&
			this.displayItems.every((item) => this.isItemSelected(item));
	}

	// Selection methods
	toggleSelectAll(): void {
		const newState = !this.selectAll;
		this.displayItems.forEach((item) => {
			const itemId = this.getItemId(item);
			if (itemId) {
				this.selectedItems[itemId] = newState;
			}
		});
		this.refreshSelectionState();
	}

	toggleItemSelection(item: SimpleAttestationFinFormation): void {
		const itemId = this.getItemId(item);
		if (!itemId) return;

		this.selectedItems[itemId] = !this.selectedItems[itemId];
		this.refreshSelectionState();
	}

	// Bulk action handlers
	handleBulkGenerate(): void {
		if (!this.selectedTemplate) return;

		const selectedKeys = Object.keys(this.selectedItems).filter(
			(key) => this.selectedItems[key]
		);

		if (selectedKeys.length === 0) {
			// No selection, generate for all
			this.emitBulkGenerate.emit({
				selectedIds: [],
				partialRespondantIds: [],
				docType: 'attestation-fin-formation',
				template: this.selectedTemplate,
				totalTimeOverride: this.globalTimeOverride,
			});
		} else {
			// Generate for selected items
			const selectedIds = selectedKeys.filter(
				(key) => !key.startsWith('partial_')
			);
			const partialRespondantIds = selectedKeys
				.filter((key) => key.startsWith('partial_'))
				.map((key) => key.replace('partial_', ''));

			this.emitBulkGenerate.emit({
				selectedIds,
				partialRespondantIds,
				docType: 'attestation-fin-formation',
				template: this.selectedTemplate,
				totalTimeOverride: this.globalTimeOverride,
			});
		}

		this.showGenerateModal = false;
		this.totalTimeOverride = null;
	}

	handleBulkSend(): void {
		const selectedKeys = Object.keys(this.selectedItems).filter(
			(key) => this.selectedItems[key]
		);

		if (selectedKeys.length === 0) {
			// No selection, send all sendable items
			const allIds = this.displayItems
				.filter((item) => item._id && this.canSendItem(item))
				.map((item) => item._id);
			this.emitBulkSend.emit(allIds);
		} else {
			// Send only selected items that are sendable
			const selectedIds = selectedKeys
				.filter((key) => !key.startsWith('partial_'))
				.filter((id) => {
					const item = this.displayItems.find(
						(item) => item._id === id
					);
					return item && this.canSendItem(item);
				});
			this.emitBulkSend.emit(selectedIds);
		}
	}

	handleBulkDelete(): void {
		const selectedKeys = Object.keys(this.selectedItems).filter(
			(key) => this.selectedItems[key]
		);

		if (selectedKeys.length === 0) return;

		// Delete only selected non-partial items with documents
		const selectedIds = selectedKeys
			.filter((key) => !key.startsWith('partial_'))
			.filter((id) => {
				const item = this.displayItems.find((item) => item._id === id);
				return item && item.document;
			});

		if (selectedIds.length === 0) return;

		// Show confirmation dialog
		const message =
			selectedIds.length === 1
				? this.translateService.instant('bulk_delete_confirm_single')
				: this.translateService.instant(
						'bulk_delete_confirm_multiple',
						{ count: selectedIds.length }
				  );

		this.nzModalService.confirm({
			nzTitle: this.translateService.instant('bulk_delete_confirm_title'),
			nzContent: message,
			nzOkText: this.translateService.instant('common_delete'),
			nzOkType: 'danger',
			nzCancelText: this.translateService.instant('common_cancel'),
			nzOnOk: () => {
				this.emitBulkDelete.emit(selectedIds);
			},
		});
	}

	handleBulkDownload(): void {
		const selectedKeys = Object.keys(this.selectedItems).filter(
			(key) => this.selectedItems[key]
		);

		if (selectedKeys.length === 0) {
			// No selection, download all downloadable items
			const allDownloadableItems = this.displayItems
				.filter((item) => item._id && this.canDownload(item))
				.map((item) => ({
					attestationId: item._id,
					filename: this.getDocumentLabel(item),
				}));
			this.emitBulkDownload.emit(allDownloadableItems);
		} else {
			// Download only selected items that are downloadable
			const selectedDownloadableItems = selectedKeys
				.filter((key) => !key.startsWith('partial_'))
				.map((id) => {
					const item = this.displayItems.find(
						(item) => item._id === id
					);
					return item && this.canDownload(item)
						? {
								attestationId: item._id,
								filename: this.getDocumentLabel(item),
						  }
						: null;
				})
				.filter((item) => item !== null);

			if (selectedDownloadableItems.length > 0) {
				this.emitBulkDownload.emit(selectedDownloadableItems);
			}
		}
	}
}
